import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { type AppointmentCategories } from 'features/Schedule/api/schedule.types'
import { type AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import { type StatusColorName } from 'features/SettingsOld/state/api/types'

export const getAppointmentColorInputFromEvent = (
  data: Pick<IPatientSchedule, 'id' | 'status' | 'category'>,
  schedules: IPatientSchedule[],
): { statusCode?: number; categoryCode?: number } => {
  const fromSchedules = schedules.find((s) => s.id === data.id || Number(s.id) === Number(data.id))
  return {
    statusCode: data.status?.code ?? fromSchedules?.status?.code,
    categoryCode: data.category?.code ?? fromSchedules?.category?.code,
  }
}

export const getAppointmentColor = ({
  appointmentColor,
  categoryStatuses,
  statusColorName,
  statusCode,
  categoryCode,
}: {
  appointmentColor: AppointmentColorType
  categoryStatuses: AppointmentCategories[]
  statusColorName: StatusColorName[]
  statusCode?: number
  categoryCode?: number
}) => {
  const status = Array.isArray(statusColorName)
    ? statusColorName.find((s) => s.code === statusCode || Number(s.code) === Number(statusCode))
    : undefined
  const category = Array.isArray(categoryStatuses)
    ? categoryStatuses.find(
        (c) => c.code === categoryCode || Number(c.code) === Number(categoryCode),
      )
    : undefined

  if (appointmentColor === 'status') {
    return {
      color: status?.color,
      background: status?.background,
      borderColor: category?.color,
    }
  }
  return { color: category?.color, background: category?.background, borderColor: status?.color }
}

type ScheduleUpdateOverrides = {
  room: string | null | undefined
  start_time: string
  end_time: string
}

export const buildScheduleUpdatePayload = (
  event: IPatientSchedule,
  { room, start_time, end_time }: ScheduleUpdateOverrides,
) => ({
  date: event.date,
  room,
  start_time,
  end_time,
  temperature_bypass: event.temperature_bypass,
  consultation_staff: event.consultation_staff,
  procedure_code: !event.procedure?.isCustom ? event.procedure?.code : null,
  procedure_description: event.procedure?.description,
  category_type_code: event.category?.code,
  title: event.title,
  comments: event.comments,
  punctuality_type_code: Number(event.punctuality?.appointment?.code || 0),
  punctuality_minutes: event.punctuality?.appointment?.minutes || 0,
  status_type_code: event.status?.code,
  patient: {
    id: event.patient?.id,
    first_name: event.patient?.first_name,
    last_name: event.patient?.last_name,
    phone: event.patient?.phone,
    language_type_code: event.patient?.language_type_code,
  },
})

type DropPointArgs = {
  target?: Element
  event?: unknown
}

export const getDropPointElement = ({ target, event }: DropPointArgs): Element | null => {
  if (target instanceof Element) return target
  const raw = ((event as { event?: Event })?.event ?? event) as
    | (MouseEvent & Partial<TouchEvent>)
    | undefined
  if (!raw) return null
  const touch = raw.changedTouches?.[0]
  const clientX = touch?.clientX ?? raw.clientX
  const clientY = touch?.clientY ?? raw.clientY
  if (typeof clientX !== 'number' || typeof clientY !== 'number') return null
  return document.elementFromPoint(clientX, clientY)
}

export const snapToFiveMinutes = (date: Date): Date => {
  const snappedDate = new Date(date)
  const minutes = snappedDate.getMinutes()
  const remainder = minutes % 5

  if (remainder === 0) {
    return snappedDate
  }
  const adjustment = remainder < 3 ? -remainder : 5 - remainder
  snappedDate.setMinutes(minutes + adjustment)
  snappedDate.setSeconds(0)
  snappedDate.setMilliseconds(0)

  return snappedDate
}

export const SCHEDULE_COLUMN_WIDTH_PX = {
  base: 170,
  stdSmUp: 280,
} as const

export const getBaseScheduleColumnWidth = (): number =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 576px)').matches
    ? SCHEDULE_COLUMN_WIDTH_PX.stdSmUp
    : SCHEDULE_COLUMN_WIDTH_PX.base

type TimeInterval = { start: number; end: number }

export const getMaxConcurrentIntervals = (intervals: TimeInterval[]): number => {
  if (intervals.length === 0) return 1

  const points: Array<{ time: number; delta: number }> = []
  for (const { start, end } of intervals) {
    if (end <= start) continue
    points.push({ time: start, delta: 1 })
    points.push({ time: end, delta: -1 })
  }

  if (points.length === 0) return 1

  points.sort((a, b) => a.time - b.time || a.delta - b.delta)

  let current = 0
  let max = 0
  for (const point of points) {
    current += point.delta
    max = Math.max(max, current)
  }

  return Math.max(1, max)
}

export const getMaxConcurrentByColumnKey = (
  events: Array<Pick<IPatientSchedule, 'room_id' | 'start_time' | 'end_time'>>,
): Map<string, number> => {
  const intervalsByColumn = new Map<string, TimeInterval[]>()

  for (const event of events) {
    const key = event.room_id
    if (!key) continue

    const start = new Date(event.start_time).getTime()
    const end = new Date(event.end_time).getTime()
    if (Number.isNaN(start) || Number.isNaN(end)) continue

    const list = intervalsByColumn.get(key)
    if (list) {
      list.push({ start, end })
    } else {
      intervalsByColumn.set(key, [{ start, end }])
    }
  }

  const result = new Map<string, number>()
  for (const [key, intervals] of intervalsByColumn) {
    result.set(key, getMaxConcurrentIntervals(intervals))
  }
  return result
}

type ApplyColumnWidthsArgs = {
  scheduleElement: HTMLElement
  columnKeys: string[]
  maxConcurrentByColumn: Map<string, number>
  baseWidthPx?: number
}

export const applyScheduleColumnWidths = ({
  scheduleElement,
  columnKeys,
  maxConcurrentByColumn,
  baseWidthPx = getBaseScheduleColumnWidth(),
}: ApplyColumnWidthsArgs): void => {
  if (columnKeys.length === 0) return

  const headerCols = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-date-header-wrap table col',
  )
  const contentCols = scheduleElement.querySelectorAll<HTMLTableColElement>(
    '.e-vertical-view .e-content-wrap table col',
  )

  columnKeys.forEach((key, index) => {
    const multiplier = maxConcurrentByColumn.get(key) ?? 1
    const width = `${baseWidthPx * multiplier}px`
    if (headerCols[index]) headerCols[index].style.width = width
    if (contentCols[index]) contentCols[index].style.width = width
  })
}

type AppointmentBox = {
  left: number
  top: number
  height: number
}

const parseStylePx = (value: string): number => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const parseStylePercent = (value: string): number => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const verticallyOverlap = (a: AppointmentBox, b: AppointmentBox): boolean =>
  a.top < b.top + b.height && b.top < a.top + a.height

export const getRightmostStretchedWidths = (boxes: AppointmentBox[]): Array<number | null> =>
  boxes.map((box, index) => {
    const hasSiblingToTheRight = boxes.some(
      (other, otherIndex) =>
        otherIndex !== index && verticallyOverlap(box, other) && other.left > box.left + 0.01,
    )
    if (hasSiblingToTheRight) return null
    return Math.max(0, 100 - box.left)
  })

export const stretchRightmostAppointmentsToColumnEdge = (scheduleElement: HTMLElement): void => {
  const wrappers = scheduleElement.querySelectorAll<HTMLElement>(
    '.e-vertical-view .e-appointment-wrapper',
  )

  wrappers.forEach((wrapper) => {
    const elements = Array.from(
      wrapper.querySelectorAll<HTMLElement>('.e-appointment:not(.e-schedule-event-clone)'),
    )
    if (elements.length === 0) return

    const boxes = elements.map((element) => ({
      left: parseStylePercent(element.style.left || '0'),
      top: parseStylePx(element.style.top || '0'),
      height: parseStylePx(element.style.height || '0'),
    }))

    const widths = getRightmostStretchedWidths(boxes)
    widths.forEach((width, index) => {
      if (width == null) return
      elements[index].style.width = `${width}%`
    })
  })
}
